import { useState, useEffect, useRef } from 'react';
import { products, categories } from '../../../mocks/products';
import ProductModal from './ProductModal';

interface SearchOverlayProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function SearchOverlay({ isOpen, onClose }: SearchOverlayProps) {
  const [query, setQuery] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<typeof products[0] | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setTimeout(() => inputRef.current?.focus(), 300);
    } else {
      document.body.style.overflow = '';
      setQuery('');
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const filtered = query.trim().length > 0
    ? products.filter((p) => {
        const q = query.toLowerCase();
        const catName = categories.find((c) => c.id === p.category)?.name || '';
        return (
          p.name.toLowerCase().includes(q) ||
          p.subcategory.toLowerCase().includes(q) ||
          catName.toLowerCase().includes(q)
        );
      })
    : [];

  const handleProductClick = (product: typeof products[0]) => {
    setSelectedProduct(product);
  };

  return (
    <>
      {/* Overlay backdrop */}
      <div
        className={`fixed inset-0 z-[80] transition-all duration-300 ${
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      >
        <div className="absolute inset-0 bg-black/80 backdrop-blur-xl" />
      </div>

      {/* Search panel */}
      <div
        className={`fixed inset-x-0 top-0 z-[85] transition-all duration-400 ${
          isOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-8 pointer-events-none'
        }`}
      >
        <div className="max-w-3xl mx-auto px-6 pt-28 pb-8">
          {/* Search input */}
          <div className="relative mb-6">
            <div className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
              <i className="ri-search-line text-white/40 text-xl"></i>
            </div>
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search products, categories..."
              className="w-full bg-[#1A1A1A] border border-white/10 rounded-2xl pl-14 pr-14 py-5 text-white text-base placeholder:text-white/30 focus:outline-none focus:border-[#FF6600]/50 focus:ring-1 focus:ring-[#FF6600]/30 transition-all duration-200"
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                className="absolute right-5 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full transition-all duration-200 cursor-pointer"
              >
                <i className="ri-close-line text-white/60 text-sm"></i>
              </button>
            )}
          </div>

          {/* Results */}
          <div
            className={`bg-[#1A1A1A] border border-white/10 rounded-2xl overflow-hidden transition-all duration-300 ${
              query.trim().length > 0 ? 'max-h-[60vh] opacity-100' : 'max-h-0 opacity-0 border-transparent'
            }`}
          >
            <div className="overflow-y-auto max-h-[60vh]">
              {filtered.length > 0 ? (
                <div className="p-3">
                  <p className="text-white/30 text-xs uppercase tracking-widest px-3 py-2 font-medium">
                    {filtered.length} result{filtered.length !== 1 ? 's' : ''} found
                  </p>
                  {filtered.map((product) => {
                    const catName = categories.find((c) => c.id === product.category)?.name || '';
                    return (
                      <button
                        key={product.id}
                        onClick={() => handleProductClick(product)}
                        className="w-full flex items-center gap-4 px-3 py-3 rounded-xl hover:bg-white/5 transition-all duration-200 cursor-pointer group text-left"
                      >
                        <div className="w-14 h-14 rounded-xl overflow-hidden bg-[#141414] flex-shrink-0">
                          <img
                            src={product.image}
                            alt={product.name}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-semibold text-sm truncate group-hover:text-[#FF6600] transition-colors duration-200">
                            {product.name}
                          </p>
                          <p className="text-white/40 text-xs mt-0.5">
                            {catName} &middot; {product.subcategory}
                          </p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-[#FF6600]/80 text-[10px] font-semibold uppercase tracking-widest italic">Prices Coming Soon</p>
                        </div>
                        <div className="w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                          <i className="ri-arrow-right-s-line text-white/40"></i>
                        </div>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="py-12 text-center">
                  <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-search-line text-white/20 text-3xl"></i>
                  </div>
                  <p className="text-white/30 text-sm">No products match &ldquo;{query}&rdquo;</p>
                </div>
              )}
            </div>
          </div>

          {/* Hint */}
          {query.trim().length === 0 && (
            <div className="text-center mt-4">
              <p className="text-white/20 text-sm">Type to search across all products and categories</p>
              <p className="text-white/15 text-xs mt-1">Press ESC to close</p>
            </div>
          )}
        </div>
      </div>

      {/* Product Modal */}
      <ProductModal
        product={selectedProduct}
        onClose={() => setSelectedProduct(null)}
      />
    </>
  );
}
